import React, { type ReactNode } from 'react';
import {
  STAT_TILE_ALIGN_START,
  STAT_TILE_LABEL_TRANSFORM_UPPERCASE,
  STAT_TILE_SIZE_MD,
  STAT_TILE_TONE_DEFAULT,
  type StatTileAlign,
  type StatTileLabelTransform,
  type StatTileSize,
  type StatTileTone,
} from './StatTile.constants';
import { Label, Root, Value } from './StatTile.styled';

export interface StatTileProps {
  /**
   * The headline number / metric. Pass any node — strings (`"97%"`), numbers
   * (`42`), or rich JSX (`<><span>1m</span> 24s</>` for compound values).
   * Tabular-nums is set on the value so columns of stats line up.
   */
  value: ReactNode;
  /** The metric's name, rendered in small uppercase below the value. */
  label: ReactNode;
  /**
   * Tone of the value text. Defaults to the surface foreground; switch to
   * `success`/`error`/`caution`/`primary` when the value carries semantic
   * weight (e.g. green for "97% success rate", red for "3 failures").
   */
  tone?: StatTileTone;
  /** Stack alignment. Defaults to `start` — matches every consumer surface. */
  align?: StatTileAlign;
  /** Vertical scale. Defaults to `md` (page-level analytics). */
  size?: StatTileSize;
  /**
   * Whether the label is automatically uppercased ("SUCCESS RATE") or
   * rendered as-is ("Success rate"). Defaults to `uppercase` to match the
   * classic stat-tile small-caps look. Pass `none` when the design
   * preserves mixed-case labels.
   */
  labelTransform?: StatTileLabelTransform;
  className?: string;
  dataTestId?: string;
}

/**
 * One "big number + small label" cell — the atomic unit of every stat grid.
 * Compose multiple `StatTile`s in a flex/grid container to build dashboards,
 * canvas-info bars, run analytics strips, etc.
 *
 * Tabular numerals are enabled on the value so vertically-stacked or
 * horizontally-aligned tiles always line up at the digit columns.
 *
 * @example
 *   <StatTile value="97%" label="Success rate" tone="success" />
 *   <StatTile value={42}  label="Total runs" />
 *   <StatTile
 *     value={<><span>1m</span> 24s</>}
 *     label="Median duration"
 *     size="lg"
 *   />
 */
const StatTile: React.FC<StatTileProps> = ({
  value,
  label,
  tone = STAT_TILE_TONE_DEFAULT,
  align = STAT_TILE_ALIGN_START,
  size = STAT_TILE_SIZE_MD,
  labelTransform = STAT_TILE_LABEL_TRANSFORM_UPPERCASE,
  className,
  dataTestId = 'stat-tile',
}) => {
  return (
    <Root
      $align={align}
      $size={size}
      className={className}
      data-testid={dataTestId}
      data-tone={tone}
      data-align={align}
      data-size={size}
    >
      <Value
        $size={size}
        $tone={tone}
        data-testid={`${dataTestId}__value`}
      >
        {value}
      </Value>
      <Label
        $size={size}
        $transform={labelTransform}
        data-testid={`${dataTestId}__label`}
      >
        {label}
      </Label>
    </Root>
  );
};

export default StatTile;
